/** @jsxImportSource @emotion/react */
import React, { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import langListStyle from './lang-list.styles';
import { LanguageFlag } from '../../assets/icons/language-flag';
import { useApp } from '../../app/app.hook';
import { selectAppState } from '../../store/app/app.selector';
import PredefinedSwipableDrawer from '../swipable-edge-drawer/swipable-drawer.component';
import { useMediaQuery } from '@mui/material';

const languageNames = ['en', 'fr', 'ua', 'es'];

type TProps = {
    zIndex?: number;
};

export const LangList: FC<TProps> = props => {
    const { zIndex } = props;
    const isDesktop = useMediaQuery('(min-width:600px)');
    const intl = useIntl();
    const { appLocale } = useApp();
    const { locale } = useSelector(selectAppState);
    const [langListIsOpen, setLangListIsOpen] = useState(false);
    const [selectedLocale, setSelectedLocale] = useState(locale);

    const toggleLangList = () => {
        setLangListIsOpen(prevState => {
            return !prevState;
        });
    };

    const onSelectLanguage = (e: ChangeEvent<HTMLInputElement>) => {
        setSelectedLocale(e.target.value);
        appLocale(e.target.value);
    };

    useEffect(() => {
        const overlayClickHandler = (e: MouseEvent) => {
            const target = e.target as Element;
            // Don't close if clicking on the button or inside the lang list
            if (
                langListIsOpen &&
                !target.closest('#langMenuButton') &&
                !target.closest('[data-lang-list]')
            ) {
                setLangListIsOpen(false);
            }
        };

        document.addEventListener('click', overlayClickHandler);

        return () => {
            document.removeEventListener('click', overlayClickHandler);
        };
    }, [langListIsOpen]);

    const langList = useMemo(() => {
        return languageNames.map(lang => (
            <label
                css={langListStyle.radioInput}
                key={lang}
                onClick={e => {
                    // Prevent event from bubbling to document click handler
                    e.stopPropagation();
                    // Only trigger if not already selected
                    if (selectedLocale !== lang) {
                        const syntheticEvent = {
                            target: { value: lang },
                        } as ChangeEvent<HTMLInputElement>;
                        onSelectLanguage(syntheticEvent);
                    }
                }}
            >
                <input
                    value={lang}
                    checked={selectedLocale == lang}
                    type="radio"
                    onChange={onSelectLanguage}
                />
                <span css={langListStyle.labelSpan}>{intl.formatMessage({ id: lang })}</span>
                <LanguageFlag locale={lang} width={20} />
            </label>
        ));
    }, [locale, languageNames, selectedLocale, onSelectLanguage, intl]);

    return (
        <div css={langListStyle.container}>
            <button
                id="langMenuButton"
                css={[langListStyle.buttonLang]}
                onClick={toggleLangList}
                aria-label={intl.formatMessage({ id: locale })}
            >
                <LanguageFlag locale={locale} />
            </button>
            {isDesktop ? (
                <>
                    {langListIsOpen && (
                        <div
                            css={[langListStyle.langList, langListStyle.langListDesktop]}
                            data-lang-list
                        >
                            {langList}
                        </div>
                    )}
                </>
            ) : (
                <PredefinedSwipableDrawer
                    zIndex={zIndex}
                    onClose={() => setLangListIsOpen(false)}
                    onOpen={() => setLangListIsOpen(true)}
                    minDrawerHeight={'40vh'}
                    openDrawerHeight={'calc(100vh - 10px)'}
                    isOpen={langListIsOpen}
                    snapPoints={[0.4]}
                >
                    <div css={langListStyle.langList} data-lang-list>
                        {langList}
                    </div>
                </PredefinedSwipableDrawer>
            )}
        </div>
    );
};
